"use client";

/**
 * Fires `blog_scroll_50` / `blog_scroll_90` (once each) as the reader scrolls
 * past 50% / 90% of the page — so PostHog shows WHERE readers drop on each post
 * (autocapture is off, so depth isn't tracked otherwise). That data drives the
 * next CTA-placement decision instead of a guess. Renders nothing.
 *
 * Consent-safe: `trackEvent` no-ops until PostHog is initialized (consent gate),
 * so this never fires for non-consented sessions. rAF-throttled, passive
 * listener, self-detaches once both milestones fire.
 */

import { useEffect, useRef } from "react";
import { trackEvent } from "@/lib/posthog";

export function BlogScrollDepth({ slug }: { slug: string }) {
  const fired = useRef({ d50: false, d90: false });

  useEffect(() => {
    let ticking = false;

    function check() {
      ticking = false;
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const pct = window.scrollY / scrollable;
      if (!fired.current.d50 && pct >= 0.5) {
        fired.current.d50 = true;
        trackEvent("blog_scroll_50", { slug });
      }
      if (!fired.current.d90 && pct >= 0.9) {
        fired.current.d90 = true;
        trackEvent("blog_scroll_90", { slug });
        window.removeEventListener("scroll", onScroll);
      }
    }

    function onScroll() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(check);
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    check(); // short posts may already exceed a threshold on load
    return () => window.removeEventListener("scroll", onScroll);
  }, [slug]);

  return null;
}
